import { useEffect, useRef } from "react";

interface ConfirmDialogProps {
  projectName: string;
  isBusy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({ projectName, isBusy, onCancel, onConfirm }: ConfirmDialogProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isBusy) onCancel();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isBusy, onCancel]);

  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={isBusy ? undefined : onCancel}
    >
      <section
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-title"
        aria-describedby="delete-description"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <span className="confirm-dialog__icon" aria-hidden="true">
          ×
        </span>
        <p className="eyebrow eyebrow--danger">Delete project?</p>
        <h2 id="delete-title">Say goodbye to “{projectName}”?</h2>
        <p id="delete-description">
          This removes the project from this device. This action cannot be undone.
        </p>
        <div className="confirm-dialog__actions">
          <button
            ref={cancelButtonRef}
            className="button button--secondary"
            type="button"
            disabled={isBusy}
            onClick={onCancel}
          >
            Keep project
          </button>
          <button
            className="button button--danger"
            type="button"
            disabled={isBusy}
            onClick={onConfirm}
          >
            {isBusy ? "Deleting…" : "Yes, delete"}
          </button>
        </div>
      </section>
    </div>
  );
}
